import * as Sentry from '@sentry/nextjs'
import Markdown from 'markdown-to-jsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { type z } from 'zod'

import { ConfigurableForm } from '@/legacy-src/components/forms/ConfigurableForm'
import { FieldType, type SectionType } from '@/legacy-src/components/forms/types'
import { createFormSchema } from '@/legacy-src/components/forms/validation'
import DoubledAccount from '@/legacy-src/components/login/DoubledAccount'
import SupportWidget from '@/legacy-src/components/support/SupportWidget'
import WarningBox from '@/legacy-src/components/WarningBox'
import { useApis } from '@/legacy-src/contexts/api-context'
import { getTldFromEnvCode } from '@/legacy-src/lib/octopus/utils'
import { cn } from '@/legacy-src/lib/utils'
import GeneralActions from '@/legacy-src/redux/GeneralRedux'
import { getApiHearmeLink } from '@/legacy-src/utilities/apiUtils'
import { getAuthPageUrl, getCompanyEmployeeFromQuery, isCompanyEmployeeInQuery } from '@/legacy-src/utilities/authUtils'
import { handleError } from '@/legacy-src/utilities/handlerError'
import { setTokens } from '@/legacy-src/utilities/setTokens'

import { useLoginParams } from '@/src/domains/auth/hooks/useLoginParams'

import Loader from '../components/Loader'
import LoginButtons from '../components/loginButtons'
import SignUpHeader from '../components/SignUpHeader'
import { Button, ButtonLink } from '../components/ui/button'
import { useGetCompany } from '../hooks/companyHooks'
import useUser from '../hooks/useUser'
import UserActions from '../redux/UserRedux'
import { useAppSelector } from '../store/hooks'
import { getTranslate } from '../utilities/i18n'

type LoginContainerPropsType = {
  therapistId?: number
}

const LoginContainer: React.FC<LoginContainerPropsType> = ({ therapistId }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { t } = getTranslate()
  const { data } = useGetCompany()
  const { isLoadingUser } = useUser()
  const { next, api } = useApis()

  const [isPending, setIsPending] = useState(false)
  const [isNonActivatedHearmeUser, setIsNonActivatedHearmeUser] = useState(false)
  const [environmentsWithUser, setEnvironmentsWithUser] = useState<string[]>([])

  const doubledAccount = router.query?.socialUser

  const { query } = router
  const emailIsInOtherCountry = useAppSelector((state) => state.general.emailIsInOtherCountry)
  const isCompanyEmployeeLogin = isCompanyEmployeeInQuery(query)

  const { email: prefilledEmail, redirectUrl } = useLoginParams()
  const defaultValues = useMemo(() => (prefilledEmail ? { email: prefilledEmail } : undefined), [prefilledEmail])

  const queryPart = decodeURIComponent(router.asPath)
  const queryParams = queryPart.split('?')?.[1]
  const registerRedirectUrl = isCompanyEmployeeLogin ? `/register/employee?${queryParams}` : '/register'

  const resetPageUrl = `/reset${queryParams ? `?${queryParams}` : ''}`

  if (isLoadingUser) {
    return <Loader visible loaderType="section-large" style={{ height: '100dvh', marginTop: '-20px' }} />
  }

  const formConfig: SectionType[] = [
    {
      fields: [
        {
          id: 'email',
          fieldType: FieldType.EMAIL,
          label: t('login.email.label'),
          required: true,
          readOnly: Boolean(prefilledEmail),
        },
        { id: 'password', fieldType: FieldType.PASSWORD, label: t('login.password.label'), required: true },
      ],
    },
  ]
  const formSchema = createFormSchema(formConfig)

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      setIsNonActivatedHearmeUser(false)
      setEnvironmentsWithUser([])
      setIsPending(true)

      const res = await next.login(
        data.email,
        data.password,
        isCompanyEmployeeLogin ? getCompanyEmployeeFromQuery(query) : undefined,
      )

      Sentry.setUser({ email: data.email })
      setTokens({
        api,
        data: {
          refreshToken: res.data.refreshToken,
          accessToken: res.data.accessToken,
        },
      })
      dispatch(GeneralActions.onChangeGeneralValue('emailIsInOtherCountry', !!res.data.emailInOtherCountry))
      dispatch(
        UserActions.getUserDataRequest(
          therapistId,
          Boolean(router.query.diagnosis as string),
          {},
          undefined,
          redirectUrl,
          router.asPath,
        ),
      )
    } catch (error: any) {
      const resError = error?.response?.data
      if (resError?.type === 'NOT_ACTIVATED') {
        // user is on instance but not activated
        setIsNonActivatedHearmeUser(true)
      } else {
        const otherCountriesWithAccount: string[] = resError?.otherCountriesWithAccount ?? []

        if (otherCountriesWithAccount.length > 0) {
          setEnvironmentsWithUser(otherCountriesWithAccount)
        } else {
          handleError('authorization', error)
        }
      }
    } finally {
      setIsPending(false)
    }
  }

  const handleSwitchToRegister = () => router.push(getAuthPageUrl('register', router.query))

  return (
    <div className="signup">
      <div className="container">
        <SignUpHeader backToIntroduction />
        <div className="signup__content">
          <div className={cn('signup__row', 'row mt-16')}>
            <div className="col-12 col-lg-6 order-lg-2 mb-10 lg:mb-0">
              {doubledAccount ? (
                <DoubledAccount />
              ) : (
                <div className="card bg-default">
                  {emailIsInOtherCountry && <EmailIsInOtherCountryNotice />}
                  <LoginButtons buttonText={t('component.button.login')} />
                  <ConfigurableForm
                    key={prefilledEmail ?? 'login'}
                    sections={formConfig}
                    onSubmit={handleSubmit}
                    formSchema={formSchema}
                    id="login-form"
                    defaultValues={defaultValues}
                  />
                  {isNonActivatedHearmeUser ? (
                    <WarningBox text={t('login.notActivated', { url: resetPageUrl })} className="mt-6" />
                  ) : environmentsWithUser.length > 0 ? (
                    environmentsWithUser.map((envCode) =>
                      isCompanyEmployeeLogin ? (
                        <WarningBox
                          key={envCode}
                          text={t('login.inAnotherCountryEmployee', {
                            url: getApiHearmeLink(
                              getTldFromEnvCode(envCode),
                              query['companyEmployee[sharedCode]'] as string,
                              query['companyEmployee[policyCode]'] as string,
                            ),
                          })}
                          className="mt-6"
                        />
                      ) : (
                        <WarningBox
                          key={envCode}
                          text={t('login.inAnotherCountry', {
                            url: `https://hedepy.${getTldFromEnvCode(envCode)}/login`,
                          })}
                          className="mt-6"
                        />
                      ),
                    )
                  ) : (
                    <ButtonLink variant="inline" className="mt-4 normal-case" href={resetPageUrl}>
                      {t('login.resetPassword.buttonText')}
                    </ButtonLink>
                  )}
                  <div className="mt-6 flex justify-center">
                    <Button loading={isPending} type="submit" form="login-form">
                      {t('login.login.submit')}
                    </Button>
                  </div>
                  <div className="mt-6 flex w-full flex-wrap justify-center gap-2 text-center">
                    <span>{t('login.register.buttonLabel')}</span>
                    <Button variant="inline" className="normal-case" onClick={handleSwitchToRegister}>
                      {t('login.register.buttonText')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="col-12 col-lg-6">
              <div className="px-6 desktop:px-10">
                <div className="signup__title">
                  {isCompanyEmployeeLogin && data?.imageUrl && (
                    <div className="mb-4 hidden max-w-[180px] big-tablet:block">
                      <img src={data?.imageUrl} alt={data.name} />
                    </div>
                  )}
                  <h1 className="h1 text-color-secondary">
                    {isCompanyEmployeeLogin ? t('login.employee.title') : t('login.title')}
                  </h1>
                  <ButtonLink href={registerRedirectUrl} variant="light" size="medium" className="mt-4">
                    {t('login.register.buttonLabel')}
                  </ButtonLink>
                </div>
                <div className="mt-6 max-w-[530px]">
                  <img alt="diagnosis-intro" className="signup__mosaic" src="/images/mosaic-removebg.png" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SupportWidget />
    </div>
  )
}

const EmailIsInOtherCountryNotice: React.FC = () => {
  const { t } = getTranslate()
  return (
    <div className="mb-10">
      <h3 className="h3 mb-4 max-w-[400px] text-error">{t('component.emailInOtherCountry.headlineLogin')}</h3>
      <p className="text-base text-text-color">
        <Markdown>{t('component.emailInOtherCountry.textLogin')}</Markdown>
      </p>
      <div className="-m-4 flex items-center">
        <div className="p-4">
          <ButtonLink size="small" newWindow href={t('component.emailInOtherCountry.switchBtn.href')}>
            {t('component.emailInOtherCountry.switchBtn.text')}
          </ButtonLink>
        </div>
        <div className="p-4">
          <Link
            href={t('component.emailInOtherCountry.infoBtn.href')}
            className="text-center font-bold underline"
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('component.emailInOtherCountry.infoBtn.text')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default LoginContainer
